import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrgFormFields, BrgFormValues, BrgFormErrors } from '../../components/brg/BrgFormFields';
import { useCreateBrg } from '../../hooks/useBrgMutations';
import { useRoles } from '../../hooks/useRoles';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ApiError, UserDropdownDto } from '../../types';

const emptyForm: BrgFormValues = {
  name: '',
  description: '',
  roleAssignments: {},
};

export function CreateBrgPage() {
  const navigate = useNavigate();
  const createMutation = useCreateBrg();
  const rolesQuery = useRoles();

  const [values, setValues] = useState<BrgFormValues>(emptyForm);
  const [errors, setErrors] = useState<BrgFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (rolesQuery.isLoading) {
    return <LoadingSpinner message="Loading..." fullPage />;
  }

  const roles = rolesQuery.data ?? [];

  function handleFieldChange(field: 'name' | 'description', value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError(null);
  }

  function handleRoleChange(roleId: number, users: UserDropdownDto[]) {
    setValues((prev) => ({
      ...prev,
      roleAssignments: { ...prev.roleAssignments, [roleId]: users },
    }));
    setErrors((prev) => {
      const newRoleErrors = { ...prev.roleErrors };
      delete newRoleErrors[roleId];
      return { ...prev, roleErrors: newRoleErrors };
    });
    setSubmitError(null);
  }

  function validate(): BrgFormErrors {
    const errs: BrgFormErrors = {};
    if (!values.name.trim()) errs.name = 'Please complete all mandatory fields';
    if (!values.description.trim()) errs.description = 'Please complete all mandatory fields';
    const roleErrors: Record<number, string> = {};
    for (const role of roles) {
      if (role.isRequired && (!values.roleAssignments[role.roleId] || values.roleAssignments[role.roleId].length === 0)) {
        roleErrors[role.roleId] = 'Please complete all mandatory fields';
      }
    }
    if (Object.keys(roleErrors).length > 0) errs.roleErrors = roleErrors;
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setSubmitError(null);

    try {
      // Flatten role assignments: each user gets their own entry
      const roleAssignments = Object.entries(values.roleAssignments)
        .flatMap(([roleId, users]) => 
          (users ?? []).map(user => ({ roleId: Number(roleId), userId: user.userId }))
        );

      await createMutation.mutateAsync({
        name: values.name.trim(),
        description: values.description.trim(),
        roleAssignments,
      });
      void navigate('/admin');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError('A BRG with this name already exists.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    }
  }

  function handleCancel() {
    void navigate(-1);
  }

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '32px 40px',
            marginBottom: '24px',
          }}
        >
          <BrgFormFields
            values={values}
            errors={errors}
            roles={roles}
            onChange={handleFieldChange}
            onRoleChange={handleRoleChange}
            disabled={createMutation.isPending}
          />
        </div>

        {submitError && (
          <div
            role="alert"
            style={{
              color: '#c5192d',
              fontSize: '0.95rem',
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '4px',
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={createMutation.isPending}
            style={{
              padding: '10px 28px',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#333',
              cursor: createMutation.isPending ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              opacity: createMutation.isPending ? 0.6 : 1,
            }}
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            style={{
              padding: '10px 28px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: createMutation.isPending ? '#cccccc' : '#0078d4',
              color: '#fff',
              cursor: createMutation.isPending ? 'wait' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              transition: 'background-color 0.2s',
            }}
          >
            {createMutation.isPending ? 'CREATING...' : 'CREATE BRG'}
          </button>
        </div>
      </form>
    </div>
  );
}
